import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Code, Smartphone, Brain, Cloud, GraduationCap, Settings,
  ArrowRight, ChevronRight, Mail, Phone, MapPin,
  Clock, Send, Check, Zap, Shield, Headphones, DollarSign,
  Users, Award, GraduationCap as Grad,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Chatbot from '../components/Chatbot';
import { internshipPrograms } from '../data/internships';
import { allProjects } from '../data/projects';

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.11 } } };

function Counter({ target, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    const end = parseInt(target);
    const step = Math.max(1, Math.ceil(end / 80));
    let cur = 0;
    const t = setInterval(() => {
      cur = Math.min(cur + step, end);
      setCount(cur);
      if (cur >= end) clearInterval(t);
    }, 16);
    return () => clearInterval(t);
  }, [inView, target]);
  return <span ref={ref}>{count}{suffix}</span>;
}

const services = [
  { icon: Code,          title: 'Web Development',      desc: 'Responsive, high-performance web apps with React.js, Next.js, and Node.js.',          color: 'bg-blue-500',   link: null },
  { icon: Smartphone,    title: 'Mobile Apps',          desc: 'Cross-platform iOS & Android apps using React Native, published to Play Store.',       color: 'bg-purple-500', link: null },
  { icon: Brain,         title: 'AI & ML Solutions',    desc: 'Intelligent automation, predictive analytics, and custom ML model deployment.',         color: 'bg-rose-500',   link: null },
  { icon: Cloud,         title: 'Cloud Infrastructure', desc: 'Scalable, secure cloud setups on AWS, Azure, and GCP with full DevOps pipelines.',      color: 'bg-teal-500',   link: null },
  { icon: GraduationCap, title: 'Industrial Training',  desc: 'Certification programs bridging academia and industry with hands-on real projects.',     color: 'bg-amber-500',  link: null },
  { icon: Users,         title: 'Campus2Training',       desc: 'Bootcamps, tech talks, and workshops delivered directly at your college campus.',       color: 'bg-green-500',  link: '/campus2training' },
  { icon: Settings,      title: 'IT Consulting',        desc: 'Strategic technology advisory to modernise your business operations and workflows.',     color: 'bg-indigo-500', link: null },
];

const FEATURED_COUNT = 4;

const whyUs = [
  { icon: GraduationCap, title: 'Education Expertise',  desc: 'Proven record building EdTech platforms for institutions across India and Australia.' },
  { icon: Zap,           title: 'Modern Tech Stack',   desc: 'React.js, Node.js, React Native — battle-tested, scalable technologies.' },
  { icon: MapPin,        title: 'Local Presence',      desc: 'Based in Karnataka, we offer on-site support and understanding of local needs.' },
  { icon: Shield,        title: 'Security First',      desc: 'OWASP practices, encryption, and regular audits built into every project.' },
  { icon: DollarSign,    title: 'Transparent Pricing', desc: 'Clear, fixed-scope quotes. No hidden fees. Flexible payment milestones.' },
  { icon: Headphones,    title: '24/7 Support',        desc: 'Responsive support team available around the clock for your business.' },
];

const techRow1 = [
  'React.js', 'Next.js', 'Node.js', 'Express.js', 'TypeScript', 'JavaScript',
  'MongoDB', 'MySQL', 'PostgreSQL', 'Redis', 'GraphQL', 'REST API',
  'Flutter', 'React Native', 'Dart', 'Swift', 'Kotlin', 'Firebase',
  'AWS', 'Azure', 'GCP', 'Docker', 'Kubernetes', 'Terraform', 'GitHub Actions', 'Tailwind CSS',
];

const techRow2 = [
  'Python', 'TensorFlow', 'PyTorch', 'Scikit-learn', 'Pandas', 'NumPy', 'Keras', 'OpenCV',
  'LangChain', 'OpenAI API', 'Hugging Face', 'RAG', 'Pinecone', 'GPT-4',
  'Selenium', 'Postman', 'TestNG', 'Cypress', 'Appium', 'JIRA',
  'Jenkins', 'CI/CD', 'Ansible', 'Prometheus', 'Grafana',
  'Arduino', 'Raspberry Pi', 'MQTT', 'ESP32', 'C/C++', 'ARM Cortex', 'RTOS', 'STM32',
  'Siemens S7', 'SCADA', 'HMI', 'Ladder Logic', 'Allen Bradley',
];

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#060D1F]">
      <Navbar />

      {/* HERO */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-[#060D1F]">
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-orange-500/15 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-blue-600/15 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '40px 40px' }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-8">
              <motion.div variants={fadeUp}>
                <span className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium">
                  <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
                  Software Development · Training · Innovation
                </span>
              </motion.div>

              <motion.h1 variants={fadeUp} className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight">
                We Build the<br />
                <span className="text-orange-500">Digital Future</span><br />
                With Purpose
              </motion.h1>

              <motion.p variants={fadeUp} className="text-lg text-slate-400 leading-relaxed max-w-lg">
                CALLOC Technologies crafts world-class software, mobile apps, and AI solutions — while training the next generation of developers right here in Karnataka.
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
                <motion.button
                  whileHover={{ scale: 1.04, boxShadow: '0 0 30px rgba(249,115,22,0.4)' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-xl font-semibold flex items-center gap-2 transition-colors"
                >
                  View Our Work <ArrowRight className="w-4 h-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border border-white/20 text-white hover:bg-white/5 px-8 py-4 rounded-xl font-semibold flex items-center gap-2 transition-colors"
                >
                  <Mail className="w-4 h-4" /> Talk to Us
                </motion.button>
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-wrap gap-8 pt-2">
                {[{ val: '12+', label: 'Projects' }, { val: '1000+', label: 'Users' }, { val: '98%', label: 'Satisfaction' }].map((s) => (
                  <div key={s.label} className="text-center">
                    <div className="text-2xl font-bold text-white">{s.val}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* floating card */}
            <motion.div initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="relative hidden lg:block">
              <motion.div animate={{ y: [0, -14, 0] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}>
                <div className="glass rounded-3xl p-6 space-y-5">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-semibold text-sm">Live Project Stats</span>
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {[{ label: 'Active Projects', val: '4', icon: '🚀' }, { label: 'Happy Clients', val: '12+', icon: '⭐' }, { label: 'Interns Placed', val: '38+', icon: '🎓' }, { label: 'Lines of Code', val: '500K+', icon: '💻' }].map((s) => (
                      <div key={s.label} className="bg-white/5 rounded-2xl p-4 text-center">
                        <div className="text-2xl mb-1">{s.icon}</div>
                        <div className="text-xl font-bold text-white">{s.val}</div>
                        <div className="text-xs text-slate-400 mt-1">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
              <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                className="absolute -bottom-8 -left-8 glass rounded-2xl px-5 py-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center"><MapPin className="w-5 h-5 text-white" /></div>
                <div><div className="text-white font-semibold text-sm">Mysore, Karnataka</div><div className="text-slate-400 text-xs">Serving India & beyond</div></div>
              </motion.div>
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#060D1F] to-transparent" />
      </section>

      {/* TECH MARQUEE */}
      <div className="bg-[#080F1E] border-y border-white/8 py-4 space-y-3">
        {/* Row 1 — scrolls left */}
        <div className="overflow-hidden">
          <div className="flex w-max animate-marquee">
            {[...techRow1, ...techRow1].map((t, i) => (
              <span key={i} className="inline-flex items-center gap-2 mx-6 text-sm text-slate-400 font-medium whitespace-nowrap">
                <span className="w-1.5 h-1.5 bg-orange-400 rounded-full flex-shrink-0" />{t}
              </span>
            ))}
          </div>
        </div>
        {/* Row 2 — scrolls right */}
        <div className="overflow-hidden">
          <div className="flex w-max animate-marquee-reverse">
            {[...techRow2, ...techRow2].map((t, i) => (
              <span key={i} className="inline-flex items-center gap-2 mx-6 text-sm text-slate-500 font-medium whitespace-nowrap">
                <span className="w-1.5 h-1.5 bg-blue-400 rounded-full flex-shrink-0" />{t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* STATS */}
      <section className="bg-[#060D1F] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 lg:divide-x divide-white/10">
            {[
              { val: '12', suffix: '+', label: 'Projects Delivered', sub: 'Across education, healthcare & more' },
              { val: '1000', suffix: '+', label: 'Active Users', sub: 'On our deployed platforms' },
              { val: '3', suffix: '+', label: 'Years of Excellence', sub: 'Building since 2023' },
              { val: '38', suffix: '+', label: 'Interns Placed', sub: 'In top tech companies' },
            ].map((s) => (
              <motion.div key={s.label} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center lg:px-10">
                <div className="text-5xl font-bold text-orange-400 mb-2"><Counter target={s.val} suffix={s.suffix} /></div>
                <div className="font-semibold text-white mb-1">{s.label}</div>
                <div className="text-sm text-slate-500">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-24 bg-[#080F1E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-8 order-2 lg:order-1">
              <div>
                <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">About CALLOC</motion.span>
                <motion.h2 variants={fadeUp} className="text-4xl lg:text-5xl font-bold text-white mt-3">Technology with a <span className="text-orange-500">Human Touch</span></motion.h2>
              </div>
              <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed">Founded in 2023, CALLOC Technologies is built on a simple belief: great technology should be accessible to every business, and great careers to every passionate learner.</motion.p>
              <motion.div variants={stagger} className="space-y-4">
                {[
                  { title: 'Our Mission', desc: 'Empower businesses worldwide with technology that drives real growth.' },
                  { title: 'Our Vision', desc: 'Become the most trusted tech partner and training hub in South India.' },
                  { title: 'Our Values', desc: 'Innovation, integrity, and excellence — in everything we ship.' },
                ].map((item) => (
                  <motion.div key={item.title} variants={fadeUp} className="flex gap-4 items-start">
                    <div className="w-6 h-6 rounded-full bg-orange-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-orange-500" />
                    </div>
                    <div><span className="font-semibold text-white">{item.title} —</span>{' '}<span className="text-slate-400">{item.desc}</span></div>
                  </motion.div>
                ))}
              </motion.div>
              <motion.button variants={fadeUp} whileHover={{ scale: 1.03 }}
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-7 py-3.5 rounded-xl font-semibold transition-colors">
                Work With Us <ArrowRight className="w-4 h-4" />
              </motion.button>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="relative order-1 lg:order-2">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=700&h=520&fit=crop&q=80" alt="CALLOC office" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060D1F]/80 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-white font-bold text-xl">Modern Workspace</div>
                  <div className="text-slate-300 text-sm mt-1">Mysore, Karnataka</div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-orange-500 rounded-3xl opacity-20 blur-xl" />
              <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 4, repeat: Infinity }}
                className="absolute -right-5 top-10 bg-[#0C1628] rounded-2xl border border-white/10 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center"><Award className="w-5 h-5 text-white" /></div>
                  <div><div className="font-bold text-white text-sm">98%</div><div className="text-slate-400 text-xs">Client Satisfaction</div></div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 bg-[#060D1F]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-2xl mb-16">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">What We Do</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl lg:text-5xl font-bold text-white mt-3">Our <span className="text-orange-500">Services</span></motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-4 text-lg">From idea to deployment — we handle every layer of your technology stack.</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc) => {
              const Icon = svc.icon;
              return (
                <motion.div key={svc.title} variants={fadeUp} whileHover={{ y: -6, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}
                  onClick={() => svc.link && navigate(svc.link)}
                  className={`group bg-[#0C1628] border border-white/10 rounded-2xl p-7 transition-all duration-300 hover:border-orange-500/40 ${svc.link ? 'cursor-pointer' : ''}`}>
                  <div className={`w-12 h-12 ${svc.color} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white text-lg mb-2 group-hover:text-orange-400 transition-colors">{svc.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{svc.desc}</p>
                  {svc.link && <div className="mt-4 flex items-center gap-1 text-orange-400 text-sm font-semibold">Learn More <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>}
                  <div className="mt-5 h-0.5 w-0 bg-orange-500 group-hover:w-full transition-all duration-500 rounded-full" />
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="py-24 bg-[#080F1E] relative overflow-hidden">
        <div className="absolute top-20 right-20 w-72 h-72 bg-orange-500/10 rounded-full blur-[80px]" />
        <div className="absolute bottom-20 left-20 w-72 h-72 bg-blue-600/10 rounded-full blur-[80px]" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Portfolio</motion.span>
              <motion.h2 variants={fadeUp} className="text-4xl lg:text-5xl font-bold text-white mt-3">Our <span className="text-orange-500">Work</span></motion.h2>
              <motion.p variants={fadeUp} className="text-slate-400 mt-3 text-sm">Click any card to see full details</motion.p>
            </div>
            <motion.button
              variants={fadeUp}
              whileHover={{ scale: 1.04 }}
              onClick={() => navigate('/projects')}
              className="flex-shrink-0 flex items-center gap-2 border border-orange-500/40 text-orange-400 hover:bg-orange-500 hover:text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all"
            >
              View All {allProjects.length} Projects <ArrowRight className="w-4 h-4" />
            </motion.button>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {allProjects.slice(0, FEATURED_COUNT).map((proj, i) => (
              <motion.div
                key={proj.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                onClick={() => navigate('/projects')}
                className="group relative rounded-2xl overflow-hidden bg-[#0C1628] border border-white/10 hover:border-orange-500/40 cursor-pointer transition-all duration-300"
              >
                <div className="relative h-56 overflow-hidden">
                  <img src={proj.image} alt={proj.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#060D1F] via-[#060D1F]/50 to-transparent" />
                  <span className="absolute top-4 right-4 bg-orange-500/90 text-white text-xs font-semibold px-3 py-1 rounded-full">{proj.cat}</span>
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity bg-orange-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1">
                    View Details <ChevronRight className="w-3 h-3" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-white font-bold text-xl mb-2">{proj.name}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">{proj.desc}</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {proj.tech.map((t) => <span key={t} className="bg-white/5 border border-white/10 text-slate-300 text-xs px-3 py-1 rounded-full">{t}</span>)}
                  </div>
                  <div className="flex items-center gap-2 text-orange-400 text-xs font-medium">
                    <Users className="w-3.5 h-3.5" />{proj.scale}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mt-10">
            <button
              onClick={() => navigate('/projects')}
              className="bg-white/5 hover:bg-orange-500 border border-white/10 hover:border-orange-500 text-white px-8 py-3.5 rounded-xl font-semibold text-sm transition-all duration-300 inline-flex items-center gap-2"
            >
              See All {allProjects.length} Projects <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* INTERNSHIP PREVIEW */}
      <section id="internship" className="py-24 bg-[#060D1F] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-500/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-[80px]" />
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '32px 32px' }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-400 font-semibold text-sm uppercase tracking-widest">Internship Programs</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl lg:text-5xl font-bold text-white mt-3">Launch Your <span className="text-orange-400">Tech Career</span></motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-4 text-lg max-w-2xl mx-auto">Work on real projects, get mentored by professionals, and land your first tech job. 38+ students placed.</motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {internshipPrograms.slice(0, 3).map((prog) => (
              <motion.div key={prog.id} variants={fadeUp} whileHover={{ y: -8 }}
                onClick={() => navigate(`/internships/${prog.id}`)}
                className="cursor-pointer glass rounded-2xl p-6 group hover:border-orange-400/40 transition-all duration-300">
                <div className="relative rounded-xl overflow-hidden mb-5 h-40">
                  <img src={prog.coverImage} alt={prog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <span className={`absolute top-3 left-3 ${prog.iconBg} text-white text-xs font-bold px-2.5 py-1 rounded-lg`}>{prog.level}</span>
                </div>
                <h3 className="text-white font-bold text-lg mb-2 group-hover:text-orange-400 transition-colors">{prog.title}</h3>
                <p className="text-slate-400 text-sm mb-4">{prog.shortDesc}</p>
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {prog.duration}</span>
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {prog.studentsCount} interns</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {prog.skills.slice(0, 3).map((sk) => <span key={sk} className="bg-white/8 text-slate-300 text-xs px-2.5 py-1 rounded-md">{sk}</span>)}
                  {prog.skills.length > 3 && <span className="bg-white/8 text-slate-300 text-xs px-2.5 py-1 rounded-md">+{prog.skills.length - 3}</span>}
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center">
            <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} onClick={() => navigate('/internships')}
              className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-xl font-semibold inline-flex items-center gap-2 transition-colors">
              <Grad className="w-5 h-5" /> View All Programs <ArrowRight className="w-4 h-4" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-24 bg-[#080F1E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-6 lg:sticky lg:top-28">
              <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Why CALLOC</motion.span>
              <motion.h2 variants={fadeUp} className="text-4xl lg:text-5xl font-bold text-white">Why 12+ businesses <span className="text-orange-500">trust us</span></motion.h2>
              <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed">We don't just write code — we become your technology partner, understanding your business deeply and delivering solutions that move the needle.</motion.p>
              <motion.button variants={fadeUp} whileHover={{ scale: 1.03 }}
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-7 py-3.5 rounded-xl font-semibold transition-colors">
                Start a Conversation <ArrowRight className="w-4 h-4" />
              </motion.button>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid gap-4">
              {whyUs.map((item) => {
                const Icon = item.icon;
                return (
                  <motion.div key={item.title} variants={fadeUp} whileHover={{ x: 6 }}
                    className="flex gap-5 items-start p-5 rounded-2xl border border-white/10 hover:border-orange-500/30 hover:bg-orange-500/5 transition-all duration-300">
                    <div className="w-11 h-11 bg-orange-500/15 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1">{item.title}</h3>
                      <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 bg-[#060D1F]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-8">
              <div>
                <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Contact</motion.span>
                <motion.h2 variants={fadeUp} className="text-4xl lg:text-5xl font-bold text-white mt-3">Let's Build <span className="text-orange-500">Together</span></motion.h2>
                <motion.p variants={fadeUp} className="text-slate-400 mt-4 text-lg leading-relaxed">Have a project or just want to learn more? We'd love to hear from you.</motion.p>
              </div>
              <motion.div variants={stagger} className="space-y-4">
                {[
                  { icon: Mail,   label: 'Email',  details: ['officecalloc@gmail.com', 'support@calloctech.com'] },
                  { icon: Phone,  label: 'Phone',  details: ['+91 8971504091', '+91 7676654809'] },
                  { icon: MapPin, label: 'Office', details: ['#8, Hegde Layout, Alanahalli', 'Mysore – 570026, Karnataka'] },
                  { icon: Clock,  label: 'Hours',  details: ['Mon–Fri: 9 AM – 6 PM', 'Sat: 10 AM – 4 PM'] },
                ].map((c) => {
                  const Icon = c.icon;
                  return (
                    <motion.div key={c.label} variants={fadeUp} className="flex gap-4 items-start p-4 bg-[#0C1628] rounded-2xl border border-white/10">
                      <div className="w-11 h-11 bg-orange-500/15 rounded-xl flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-orange-400" /></div>
                      <div>
                        <div className="font-semibold text-white text-sm mb-1">{c.label}</div>
                        {c.details.map((d) => <div key={d} className="text-slate-400 text-sm">{d}</div>)}
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="bg-[#0C1628] rounded-3xl p-8 border border-white/10">
              <h3 className="text-2xl font-bold text-white mb-6">Send us a Message</h3>
              <form className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Name *</label>
                    <input type="text" placeholder="Your name" className="w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Email *</label>
                    <input type="email" placeholder="your@email.com" className="w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Subject *</label>
                  <input type="text" placeholder="How can we help?" className="w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Message *</label>
                  <textarea rows="5" placeholder="Tell us about your project..." className="w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all resize-none" />
                </div>
                <motion.button type="submit" whileHover={{ scale: 1.02, boxShadow: '0 8px 30px rgba(249,115,22,0.3)' }} whileTap={{ scale: 0.98 }}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors">
                  <Send className="w-4 h-4" /> Send Message
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
      <Chatbot />
    </div>
  );
}
